import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';
import { Select, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SelectContentMaxPriority } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { AnexoCard } from './AnexoCard';
import { ImagePreviewModal } from './ImagePreviewModal';
import { ConferenciaInputFixaCard } from './ConferenciaInputFixaCard';
import { type Anexo, TIPOS_ANEXO, TipoAnexo } from './types';

interface Props {
  clienteId?: string;
  pedidoId?: string;
}

export function AnexosList({ clienteId, pedidoId }: Props) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [filtroTipo, setFiltroTipo] = useState<TipoAnexo | 'TODOS'>('TODOS');
  const [imagemPreview, setImagemPreview] = useState<Anexo | null>(null);

  const { data: anexos, isLoading } = useQuery({
    queryKey: ['anexos_crm', clienteId, pedidoId, filtroTipo],
    queryFn: async () => {
      let query = supabase
        .from('anexos_crm')
        .select('*')
        .order('created_at', { ascending: false });

      // Priorizar busca por cliente_id para visão unificada
      if (clienteId) {
        query = query.eq('cliente_id', clienteId);
      } else if (pedidoId) {
        // Fallback: buscar por pedido se não tiver cliente
        const { data: pedidoData } = await supabase
          .from('pedidos')
          .select('pedido_mae_id')
          .eq('id', pedidoId)
          .single();

        if (pedidoData?.pedido_mae_id) {
          query = query.or(`pedido_id.eq.${pedidoId},pedido_id.eq.${pedidoData.pedido_mae_id}`);
        } else {
          query = query.eq('pedido_id', pedidoId);
        }
      }
      
      if (filtroTipo !== 'TODOS') query = query.eq('tipo_anexo', filtroTipo);

      const { data, error } = await query;
      if (error) throw error;

      // Buscar números dos pedidos vinculados
      const pedidoIds = [...new Set((data || []).map(a => a.pedido_id).filter(Boolean))];
      let pedidosMap: Record<string, string> = {};
      if (pedidoIds.length > 0) {
        const { data: pedidos } = await supabase
          .from('pedidos')
          .select('id, numero_pedido')
          .in('id', pedidoIds);
        if (pedidos) {
          pedidosMap = Object.fromEntries(pedidos.map(p => [p.id, String(p.numero_pedido)]));
        }
      }

      const withUrls = await Promise.all(
        (data || []).map(async (anexo) => {
          const { data: signedData, error: signErr } = await supabase.storage
            .from('anexos-crm')
            .createSignedUrl(anexo.path, 3600);

          return {
            ...anexo,
            url: signErr ? null : signedData?.signedUrl,
            _numeroPedido: anexo.pedido_id ? pedidosMap[anexo.pedido_id] : undefined,
          } as Anexo & { _numeroPedido?: string };
        })
      );

      return withUrls;
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (anexo: Anexo) => {
      const { error: storageErr } = await supabase.storage
        .from('anexos-crm')
        .remove([anexo.path]);

      if (storageErr) console.warn('Erro ao deletar do storage:', storageErr);

      const { error: dbErr } = await supabase
        .from('anexos_crm')
        .delete()
        .eq('id', anexo.id);

      if (dbErr) throw dbErr;
    },
    onSuccess: () => {
      toast({ title: 'Anexo excluído com sucesso' });
      qc.invalidateQueries({ queryKey: ['anexos_crm'] });
      if (pedidoId) qc.invalidateQueries({ queryKey: ['input-fixa-conferencias', pedidoId] });
    },
    onError: (error: Error) => {
      toast({ 
        variant: 'destructive', 
        title: 'Erro ao excluir anexo', 
        description: error.message 
      });
    },
  });

  const handleDelete = (anexo: Anexo) => {
    if (confirm(`Confirma exclusão de "${anexo.nome}"?`)) {
      deleteMut.mutate(anexo);
    }
  };

  const anexosFiltrados = anexos || [];

  // Contar anexos por tipo
  const contadores = anexosFiltrados.reduce((acc, anexo) => {
    acc[anexo.tipo_anexo] = (acc[anexo.tipo_anexo] || 0) + 1;
    return acc;
  }, {} as Record<TipoAnexo, number>);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      {pedidoId && (
        <ConferenciaInputFixaCard
          pedidoId={pedidoId}
          temPrints={anexosFiltrados.some((a) => a.tipo_anexo === 'DOCUMENTOS_INPUT_FIXA')}
        />
      )}
      <div className="mb-4">
        <Label htmlFor="filtro-tipo">Filtrar por tipo:</Label>
        <Select value={filtroTipo} onValueChange={(v) => setFiltroTipo(v as any)}>
          <SelectTrigger id="filtro-tipo" className="w-full sm:w-64 mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContentMaxPriority>
            <SelectItem value="TODOS">
              Todos os tipos ({anexosFiltrados.length})
            </SelectItem>
            {TIPOS_ANEXO.map(t => {
              const count = contadores[t.value] || 0;
              return (
                <SelectItem key={t.value} value={t.value}>
                  {t.label} {count > 0 && `(${count})`}
                </SelectItem>
              );
            })}
          </SelectContentMaxPriority>
        </Select>
      </div>

      <div className="space-y-3">
        {anexosFiltrados.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <p>Nenhum anexo encontrado</p>
            {filtroTipo !== 'TODOS' && (
              <p className="text-sm mt-1">Tente alterar o filtro</p>
            )}
          </div>
        ) : (
          anexosFiltrados.map((anexo) => (
            <AnexoCard
              key={anexo.id}
              anexo={anexo}
              numeroPedido={(anexo as any)._numeroPedido}
              onDelete={handleDelete}
              onPreview={setImagemPreview}
              deletando={deleteMut.isPending}
            />
          ))
        )}
      </div>

      <ImagePreviewModal
        imageUrl={imagemPreview?.url || null}
        imageName={imagemPreview?.nome || ''}
        onClose={() => setImagemPreview(null)}
      />
    </>
  );
}
